import {Suspense, useCallback, useEffect, useMemo} from 'react'
import {
  graphql,
  type PreloadedQuery,
  useFragment,
  usePreloadedQuery,
  useQueryLoader,
  useRelayEnvironment,
} from 'react-relay'

import {dismissRepoFirstTimeContributionBannerMutation} from '../../mutations/dismiss-first-time-contribution-banner-for-repo-mutation'
import {dismissFirstTimeContributionBannerMutation} from '../../mutations/dismiss-first-time-contribution-banner-mutation'
import type {
  FirstTimeContributionBanner$data,
  FirstTimeContributionBanner$key,
} from './__generated__/FirstTimeContributionBanner.graphql'
import type {FirstTimeContributionBannerContributingGuidelinesQuery} from './__generated__/FirstTimeContributionBannerContributingGuidelinesQuery.graphql'
import {FirstTimeContributionBannerDisplay} from './FirstTimeContributionBannerDisplay'

// Fetching the contributing guidelines link for the repository
// can be slow since it's using gitrpc, so we fetch it in a secondary query
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const FirstTimeContributionBannerContributingGuidelinesGraphqlQuery = graphql`
  query FirstTimeContributionBannerContributingGuidelinesQuery($owner: String!, $name: String!) {
    repository(owner: $owner, name: $name) {
      contributingGuidelines {
        firstTimeContributionLink
      }
    }
  }
`

export type FirstTimeContributionBannerProps = {
  repository: FirstTimeContributionBanner$key
}

export const FirstTimeContributionBanner = ({repository}: FirstTimeContributionBannerProps) => {
  const environment = useRelayEnvironment()
  const [bannerLazyDataRef, loadBannerLazyData] =
    useQueryLoader<FirstTimeContributionBannerContributingGuidelinesQuery>(
      FirstTimeContributionBannerContributingGuidelinesGraphqlQuery,
    )
  const data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment FirstTimeContributionBanner on Repository {
        id
        showFirstTimeContributorBanner(isPullRequests: false)
        nameWithOwner
        communityProfile {
          goodFirstIssueIssuesCount
        }
        url
      }
    `,
    repository,
  )

  const dismissForThisRepo = useCallback(() => {
    dismissRepoFirstTimeContributionBannerMutation({
      environment,
      repositoryId: data.id,
    })
  }, [environment, data.id])

  const dismissForAllRepos = useCallback(() => {
    dismissFirstTimeContributionBannerMutation({environment, repositoryId: data.id})
  }, [environment, data.id])

  useEffect(() => {
    if (!data.showFirstTimeContributorBanner) return

    const [owner = '', name = ''] = data.nameWithOwner.split('/')
    loadBannerLazyData({owner, name})
  }, [loadBannerLazyData, data.showFirstTimeContributorBanner, data.nameWithOwner])

  const bannerWithoutLazyData = useMemo(
    () => (
      <FirstTimeContributionBannerDisplay
        repoNameWithOwner={data.nameWithOwner}
        contributingGuidelinesUrl={undefined}
        hasGoodFirstIssueIssues={Boolean(data.communityProfile?.goodFirstIssueIssuesCount)}
        contributeUrl={`${data.url}/contribute`}
        dismissForThisRepo={dismissForThisRepo}
        dismissForAllRepos={dismissForAllRepos}
      />
    ),
    [
      data.nameWithOwner,
      data.communityProfile?.goodFirstIssueIssuesCount,
      data.url,
      dismissForThisRepo,
      dismissForAllRepos,
    ],
  )

  if (!data.showFirstTimeContributorBanner) {
    return null
  }

  if (!bannerLazyDataRef) {
    return bannerWithoutLazyData
  }

  return (
    <Suspense fallback={bannerWithoutLazyData}>
      <FirstTimeContributionBannerInternal
        queryRef={bannerLazyDataRef}
        data={data}
        dismissForThisRepo={dismissForThisRepo}
        dismissForAllRepos={dismissForAllRepos}
      />
    </Suspense>
  )
}

type FirstTimeContributionBannerInternalProps = {
  queryRef: PreloadedQuery<FirstTimeContributionBannerContributingGuidelinesQuery>
  dismissForThisRepo: () => void
  dismissForAllRepos: () => void
  data: FirstTimeContributionBanner$data
}

const FirstTimeContributionBannerInternal = ({
  data,
  queryRef,
  dismissForAllRepos,
  dismissForThisRepo,
}: FirstTimeContributionBannerInternalProps) => {
  const {repository} = usePreloadedQuery<FirstTimeContributionBannerContributingGuidelinesQuery>(
    FirstTimeContributionBannerContributingGuidelinesGraphqlQuery,
    queryRef,
  )

  return (
    <FirstTimeContributionBannerDisplay
      repoNameWithOwner={data.nameWithOwner}
      contributingGuidelinesUrl={repository?.contributingGuidelines?.firstTimeContributionLink ?? undefined}
      hasGoodFirstIssueIssues={Boolean(data.communityProfile?.goodFirstIssueIssuesCount)}
      contributeUrl={`${data.url}/contribute`}
      dismissForThisRepo={dismissForThisRepo}
      dismissForAllRepos={dismissForAllRepos}
    />
  )
}
